import React from "react";
import { CircleCheck } from "lucide-react";
import InertiaLink from "@/components/buttons/InertiaLink";
import Tag from "@/components/Tag";
import { formatStatus, statusVariant } from "./contractStatus";
import ContractPdfLink from "./ContractPdfLink";
import { Contract, Signatory } from "./types";

const formatSignedOn = (signedAt: string): string =>
  `Signed ${new Date(signedAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;

const TrailingState: React.FC<{ signatory: Signatory }> = ({ signatory }) => {
  if (signatory.sign_url) {
    return <InertiaLink href={signatory.sign_url} type="primary" text="Sign" />;
  }

  if (signatory.status === "signed") {
    return (
      <span className="text-cream-600 dark:text-cream-400 flex flex-row items-center gap-1.5 text-sm whitespace-nowrap">
        <CircleCheck
          className="text-success-700"
          size={16}
          aria-label="Signed"
        />
        {signatory.signed_at && formatSignedOn(signatory.signed_at)}
      </span>
    );
  }

  if (signatory.status === "declined") {
    return (
      <span className="text-danger-600 text-sm whitespace-nowrap">
        Declined
      </span>
    );
  }

  return (
    <span className="text-cream-600 dark:text-cream-400 text-sm whitespace-nowrap">
      Pending
    </span>
  );
};

const SignerBlock: React.FC<{ signatory: Signatory }> = ({ signatory }) => {
  const rolesText = signatory.roles.map((role) => role.label).join(" · ");

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-4 first:pt-0 last:pb-0">
      <span className="text-cream-900 dark:text-cream-100 order-1 font-medium">
        {signatory.name}
      </span>
      {rolesText && (
        <span className="text-cream-600 dark:text-cream-400 order-3 w-full text-sm md:order-2 md:w-auto">
          {rolesText}
        </span>
      )}
      <div className="order-2 ml-auto md:order-3">
        <TrailingState signatory={signatory} />
      </div>
    </li>
  );
};

interface ContractCardProps {
  contract: Contract;
  trackId: number;
}

const ContractCard: React.FC<ContractCardProps> = ({ contract, trackId }) => {
  const downloadVisible =
    Boolean(contract.document_url) || contract.pdf_generating;

  return (
    <div className="dark:bg-cream-950 flex w-full flex-col gap-4 rounded-2xl bg-white p-6 shadow">
      <div className="border-cream-200 dark:border-cream-800 flex flex-col gap-3 border-b pb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="text-cream-900 dark:text-cream-100 min-w-0 text-lg font-semibold">
            {contract.title}
          </h3>
          <Tag
            label={formatStatus(contract.status)}
            variant={statusVariant(contract.status)}
          />
        </div>
        <div className="flex w-full gap-3 md:w-auto">
          <InertiaLink
            href={`/tracks/${trackId}/contracts/${contract.id}`}
            type={downloadVisible ? "primary" : "secondary"}
            text="View"
            className="flex-1 md:flex-none"
          />
          <ContractPdfLink
            documentUrl={contract.document_url}
            pdfGenerating={contract.pdf_generating}
            variant="secondary"
            label="Download"
            className="flex-1 md:flex-none"
          />
        </div>
      </div>

      {contract.signatories.length === 0 ? (
        <p className="text-cream-600 dark:text-cream-400 text-sm">
          Signatories are derived when this contract is issued.
        </p>
      ) : (
        <ul className="divide-cream-200 dark:divide-cream-800 flex flex-col divide-y">
          {contract.signatories.map((signatory) => (
            <SignerBlock key={signatory.contributor_id} signatory={signatory} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ContractCard;
